import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Stack,
    Typography,
    Box,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import signaturesApi from 'api/rest/signatures';
import { B2B_AGREEMENT_TYPE_OPTIONS } from 'data/constants/b2bAgreement';
import useTranslate from 'hooks/useTranslate';
import { loadTemplatesForSelection } from 'redux/retainer-templates/templates.middleware';
import { loadSignatures } from 'redux/signatures/signatures.middleware';
import { AppDispatch, RootState } from 'redux/store';
import { Agent, OptionInterface, OrganizationAgreementFormInterface } from 'types';
import type { AgentSignature } from 'types/signature';
import { generateOptionFromObjectArray } from 'utils/dataFormatter';
import {
    b2bAgreementFormInitialValues,
    b2bAgreementFormValidationSchema,
} from 'utils/validations/organization';

interface Props {
    disabled?: boolean;
    title: string;
    initialData?: OrganizationAgreementFormInterface;
    agreementEmailOptions: OptionInterface[];
    myAgents: Agent[];
    onClose: () => void;
    onSubmit: (value: OrganizationAgreementFormInterface) => void | Promise<void>;
}

const SECTION_LABEL_SX = {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: 'text.disabled',
    mb: 1.5,
};

/** Converts an AgentSignature object into an HTML string suitable for PDF rendering.
 *  Mirrors backend AgentSignature.render_as_html() — always produces an <img> with
 *  a base64 data URI so xhtml2pdf can render it without SVG or web-font support. */
function agentSignatureToHtml(sig: AgentSignature): string {
    const widthMap: Record<string, string> = { small: '60px', medium: '80px', large: '100px' };
    const width = widthMap[sig.size] || '80px';

    switch (sig.creation_method) {
        case 'typed':
            if (sig.typed_text && sig.font_family) {
                // Typed text — keep as span; text is visible in PDF even if font differs
                return `<span style="font-family: '${sig.font_family}', cursive; font-size: 32px; color: ${sig.color};">${sig.typed_text}</span>`;
            }
            break;
        case 'drawn':
            if (sig.drawn_svg_data) {
                // SignaturePad embeds the drawing as a base64 image inside the SVG.
                // Extract it so xhtml2pdf gets a plain <img> it can render.
                const match = sig.drawn_svg_data.match(/data:image\/[^;]+;base64,[^"'\s>]+/);
                if (match) {
                    return `<img src="${match[0]}" style="width: ${width}; height: 35px; display: block;" alt="Signature" />`;
                }
                // Fallback: send raw SVG (backend will attempt svg→png conversion)
                return `<div style="width: ${width}; height: 35px; overflow: hidden;">${sig.drawn_svg_data}</div>`;
            }
            break;
        case 'uploaded':
            if (sig.uploaded_image_base64) {
                const mimeType = sig.uploaded_image_mime_type || 'image/png';
                return `<img src="data:${mimeType};base64,${sig.uploaded_image_base64}" style="width: ${width}; height: 35px;" alt="Signature" />`;
            }
            break;
    }
    return '';
}

/** Watches template selection and auto-sets agreement_type from the template's type.
 *  Also watches party_a_agent_id and populates signatory name + signature HTML. */
const AutoSync = ({
    templates,
    myAgents,
    signatureHtml,
    onPartyASignatureStatus,
}: {
    templates: { id: number; type: string }[];
    myAgents: Agent[];
    signatureHtml: string;
    onPartyASignatureStatus: (status: boolean | null) => void;
}) => {
    const { values, setFieldValue } = useFormikContext<OrganizationAgreementFormInterface>();

    // Auto-set agreement_type from template
    useEffect(() => {
        if (!values.template_id) return;
        const tpl = templates.find((t) => t.id === values.template_id);
        if (tpl?.type) setFieldValue('agreement_type', tpl.type);
    }, [values.template_id, templates, setFieldValue]);

    // When a party A agent is selected, populate the signatory name and fetch signature status
    useEffect(() => {
        if (!values.party_a_agent_id) {
            onPartyASignatureStatus(null);
            return;
        }
        const agent = (myAgents ?? []).find((a) => a.id === values.party_a_agent_id);
        if (!agent) return;
        const fullName = [agent.firstname, agent.middlename, agent.lastname]
            .filter(Boolean)
            .join(' ');
        setFieldValue('party_a_signatory_name', fullName);
        onPartyASignatureStatus(null); // reset while loading
        signaturesApi
            .getAgentDefaultSignature(agent.id)
            .then((res) => onPartyASignatureStatus(res.data !== null))
            .catch(() => onPartyASignatureStatus(false));
    }, [values.party_a_agent_id, myAgents, setFieldValue, onPartyASignatureStatus]);

    // Populate signature HTML from the logged-in user's default signature
    useEffect(() => {
        if (signatureHtml) setFieldValue('party_a_signature_html', signatureHtml);
    }, [signatureHtml, setFieldValue]);

    return null;
};

const AgreementForm = ({
    disabled,
    title,
    initialData,
    agreementEmailOptions,
    myAgents = [],
    onClose,
    onSubmit,
}: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { templateOptions, templatesWithType, signatureHtml } = useAgreementForm();

    const [partyAHasSignature, setPartyAHasSignature] = useState<boolean | null>(null);

    const getAgreementTypeLabel = (type: string | null) => {
        if (!type) return null;
        const label = B2B_AGREEMENT_TYPE_OPTIONS.find((o) => o.value === type)?.label;
        return label ? translate(label) : type;
    };

    return (
        <Dialog open fullWidth maxWidth="sm" fullScreen={fullScreen} scroll="paper">
            <Formik
                enableReinitialize
                initialValues={initialData ?? b2bAgreementFormInitialValues}
                validationSchema={b2bAgreementFormValidationSchema}
                onSubmit={onSubmit}
            >
                {({ values }) => {
                    return (
                        <Box
                            component={Form}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            <AutoSync
                                templates={templatesWithType}
                                myAgents={myAgents}
                                signatureHtml={signatureHtml}
                                onPartyASignatureStatus={setPartyAHasSignature}
                            />

                            <DialogTitle>
                                <Stack direction="column">
                                    {translate(title)}
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('agreementWillBeCreatedAndDownloaded')}
                                    </Typography>
                                </Stack>
                            </DialogTitle>

                            <DialogContent
                                dividers
                                sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.5 }}
                            >
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography sx={SECTION_LABEL_SX}>
                                            {translate('agreementTemplate')}
                                        </Typography>
                                        <FormField
                                            required
                                            type="autosuggest"
                                            name="template_id"
                                            label="selectTemplate"
                                            disabled={disabled}
                                            size="small"
                                            options={templateOptions}
                                        />
                                        {values.agreement_type && (
                                            <Chip
                                                size="small"
                                                label={getAgreementTypeLabel(values.agreement_type)}
                                                sx={{
                                                    backgroundColor: '#f0ebff',
                                                    color: '#6025D8',
                                                    fontWeight: 600,
                                                    fontSize: '11px',
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Divider />

                                    {/* Counter-party */}
                                    <Box>
                                        <Typography sx={SECTION_LABEL_SX}>
                                            {translate('counterPartySignatory')}
                                        </Typography>
                                        <FormField
                                            required
                                            type="autosuggest"
                                            name="party_b_signatory_email"
                                            label="selectAgent"
                                            disabled={disabled}
                                            options={agreementEmailOptions}
                                            size="small"
                                        />
                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            sx={{ mt: 0.5, display: 'block' }}
                                        >
                                            {translate('signingLinkWillBeSent')}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    {/* Party A signatory */}
                                    <Box>
                                        <Typography sx={SECTION_LABEL_SX}>
                                            {translate('yourSideSignatory')}
                                        </Typography>
                                        <FormField
                                            required
                                            type="autosuggest"
                                            name="party_a_agent_id"
                                            label="selectAgent"
                                            disabled={disabled}
                                            size="small"
                                            options={myAgents.map((agent) => {
                                                const name = [
                                                    agent.firstname,
                                                    agent.middlename,
                                                    agent.lastname,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ');
                                                return {
                                                    label: agent.email
                                                        ? `${name} (${agent.email})`
                                                        : name,
                                                    value: agent.id,
                                                };
                                            })}
                                        />
                                        {partyAHasSignature !== null && (
                                            <Typography
                                                variant="caption"
                                                color={
                                                    partyAHasSignature ? 'success.main' : 'error'
                                                }
                                                sx={{ mt: 0.5, display: 'block' }}
                                            >
                                                {partyAHasSignature
                                                    ? translate('signatureOnFile')
                                                    : translate('noSignatureConfigured')}
                                            </Typography>
                                        )}
                                        <FormField
                                            type="string"
                                            name="party_a_signatory_title"
                                            label="signatoryTitleOptional"
                                            disabled={disabled}
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    </Box>

                                    <Divider />

                                    {/* Dates */}
                                    <Box>
                                        <Typography sx={SECTION_LABEL_SX}>
                                            {translate('contractDatesOptional')}
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <FormField
                                                    type="date"
                                                    name="effective_date"
                                                    label="effectiveDate"
                                                    disabled={disabled}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <FormField
                                                    type="date"
                                                    name="expiry_date"
                                                    label="expiryDate"
                                                    disabled={disabled}
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Stack>
                            </DialogContent>

                            {partyAHasSignature === false && (
                                <Box sx={{ px: 3, pb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ display: 'block' }}
                                    >
                                        {translate('agentHasNoSignature')}
                                    </Typography>
                                </Box>
                            )}
                            <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, gap: 1 }}>
                                <Button
                                    onClick={onClose}
                                    color="inherit"
                                    disabled={disabled}
                                    sx={{ borderRadius: '8px' }}
                                >
                                    {translate('cancel')}
                                </Button>
                                <SubmitButton
                                    loading={disabled}
                                    disabled={partyAHasSignature === false}
                                >
                                    createB2bAgreement
                                </SubmitButton>
                            </DialogActions>
                        </Box>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default AgreementForm;

const useAgreementForm = () => {
    const dispatch = useDispatch<AppDispatch>();

    const { templatesForSelection: allTemplates } = useSelector(
        (state: RootState) => state.templates
    );
    const defaultSignature = useSelector((state: RootState) => state.signatures.defaultSignature);

    const signatureHtml = defaultSignature ? agentSignatureToHtml(defaultSignature) : '';

    const templateOptions = useMemo(
        () => generateOptionFromObjectArray(allTemplates, 'name', 'id'),
        [allTemplates]
    );

    const templatesWithType = useMemo(
        () => generateOptionFromObjectArray(allTemplates, 'id', 'type'),
        [allTemplates]
    );

    useEffect(() => {
        dispatch(loadSignatures());
        dispatch(loadTemplatesForSelection({ type: 'b2b' }));
    }, [dispatch]);

    return {
        templateOptions,
        templatesWithType,
        signatureHtml,
    };
};
